'use client'

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import Link from 'next/link'
import { sliderApi, SliderSlide } from '@/lib/api'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4000'

const defaultSlides: SliderSlide[] = [
  {
    id: '1',
    title: 'Welcome to Cosmicon Events',
    subtitle: 'Manage cosplay events with ease - from registration to judging',
    ctaText: 'Get Started',
    ctaLink: '/register',
    backgroundColor: '#667eea',
    orderIndex: 0,
  },
  {
    id: '2',
    title: 'Dynamic Judging System',
    subtitle: 'Flexible criteria setup with real-time score tracking for competitions',
    ctaText: 'Learn More',
    ctaLink: '/events',
    backgroundColor: '#f5576c',
    orderIndex: 1,
  },
  {
    id: '3',
    title: 'Easy Registration',
    subtitle: 'Register as a contestant or attend as a spectator in just a few clicks',
    ctaText: 'Register Now',
    ctaLink: '/register',
    backgroundColor: '#00f2fe',
    orderIndex: 2,
  },
]

export default function HomeSlider() {
  const [slides, setSlides] = useState<SliderSlide[]>(defaultSlides)
  const [current, setCurrent] = useState(0)
  const [isAutoPlay, setIsAutoPlay] = useState(true)

  useEffect(() => {
    sliderApi.listPublic()
      .then((data) => { if (data.length > 0) setSlides(data) })
      .catch(() => {})
  }, [])

  useEffect(() => {
    setCurrent(0)
  }, [slides])

  useEffect(() => {
    if (!isAutoPlay) return

    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length)
    }, 5000)

    return () => clearInterval(interval)
  }, [isAutoPlay, slides.length])

  const prev = () => {
    setCurrent((prev) => (prev - 1 + slides.length) % slides.length)
    setIsAutoPlay(false)
  }

  const next = () => {
    setCurrent((prev) => (prev + 1) % slides.length)
    setIsAutoPlay(false)
  }

  const goToSlide = (index: number) => {
    setCurrent(index)
    setIsAutoPlay(false)
  }

  return (
    <div className="relative w-full overflow-hidden rounded-lg">
      <div className="relative h-96">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === current ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {slide.imageUrl ? (
              <img
                src={slide.imageUrl.startsWith('/uploads/') ? `${API_URL}${slide.imageUrl}` : slide.imageUrl}
                alt={slide.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div
                className="absolute inset-0"
                style={{ background: `linear-gradient(135deg, ${slide.backgroundColor} 0%, ${slide.backgroundColor}cc 100%)` }}
              />
            )}
            <div className="absolute inset-0 bg-black/40" />
            <div className="relative h-full flex flex-col items-center justify-center px-6 text-center text-white">
              <h2 className="text-4xl md:text-5xl font-bold mb-3 text-balance">
                {slide.title}
              </h2>
              <p className="text-lg md:text-xl text-white/90 mb-8 max-w-md text-balance">
                {slide.subtitle}
              </p>
              <Button size="lg" asChild className="bg-white text-foreground hover:bg-white/90">
                <Link href={slide.ctaLink}>{slide.ctaText}</Link>
              </Button>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={prev}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/20 hover:bg-white/40 text-white p-2 rounded-full transition-colors"
        aria-label="Previous slide"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        onClick={next}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/20 hover:bg-white/40 text-white p-2 rounded-full transition-colors"
        aria-label="Next slide"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-2 rounded-full transition-all ${
              index === current
                ? 'bg-white w-8'
                : 'bg-white/50 w-2 hover:bg-white/75'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      <div className="absolute top-4 right-4 z-10 text-white/80 text-xs">
        {current + 1} / {slides.length}
      </div>
    </div>
  )
}
